import type { NavModule } from '@biovet/ui';

/**
 * The module map from the Biovet design, verbatim.
 *
 * **Every item names the `permission_modules` row it is.** That mapping is the
 * whole of what makes the rail follow the matrix: `visibleModules()` drops an
 * item whose module the session does not hold VIEW on, and a group left with no
 * items goes with them. It used to be one hardcoded special case — the Setup
 * module tested `can('admin.company')` in `App` and every other item rendered
 * for everybody, so a warehouse clerk was shown the Trial Balance, clicked it,
 * and got a screen full of figures their role was never granted.
 *
 * **Every item, including `overview`.** It used to be the one with no
 * `permission` — the landing page, with no `dashboard.*` module to name — and
 * that exemption had a consequence nobody was looking for: on a cold load, before
 * `GET /api/session` answers, every permission reads false, so Dashboard was the
 * only module left standing and `App` replaced whatever address the user had
 * opened with `/dashboard/overview`. `0042` gives it `dashboard.overview`,
 * granted to every role by the seed, and `App` waits for the session besides.
 *
 * **The rail is not the gate.** `READ_MODULE` in `apps/api/src/read-access.ts`
 * refuses the same reads on the server, which is what makes this real rather
 * than a set of hidden buttons — a hand-written `fetch` gets the same 403.
 *
 * **No badge is written here.** Two items used to carry one — `settlement: 2`
 * and `recon: 4` — transcribed from the design file, so the rail claimed four
 * unmatched bank lines on a book with no bank statement at all and two pending
 * settlements on a book with no deals. `withBadges()` below stamps them from
 * `GET /api/nav-counts`, and an item with nothing waiting shows no badge, which
 * is the answer a count of zero should give.
 */
export const MODULES: NavModule[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    hint: 'Position at a glance',
    items: [
      // The landing page, and the one item that named no module until 0042 —
      // which is what made Dashboard the single module a session holding
      // nothing could still see, and gave the deep-link redirect in `App` a
      // target to redirect *to*. Every role is granted it by the seed.
      { key: 'overview', label: 'Overview', tag: 'OVW', permission: 'dashboard.overview' },
      // The other four dashboard screens, each with its own module since 0046.
      // They borrowed `reports.financial` and `admin.audit`, which made "let
      // this role see the cash position" a decision the matrix could not
      // express: ticking it handed over the whole Reports library, and leaving
      // it unticked took the landing page's own tiles away.
      {
        key: 'cash-position',
        label: 'Cash & Bank Position',
        tag: 'CASH',
        permission: 'dashboard.cash_position',
      },
      {
        key: 'receivables',
        label: 'Receivables Ageing',
        tag: 'AR',
        permission: 'dashboard.receivables',
      },
      { key: 'payables', label: 'Payables Ageing', tag: 'AP', permission: 'dashboard.payables' },
      { key: 'activity', label: 'Recent Activity', tag: 'ACT', permission: 'dashboard.activity' },
    ],
  },
  {
    key: 'brokerage',
    label: 'Brokerage',
    hint: 'Deals, commission on Party A',
    items: [
      { key: 'deal', label: 'Deal Entry', tag: 'DEAL', permission: 'brokerage.deal' },
      {
        key: 'deal-register',
        label: 'Deal Register',
        tag: 'REG',
        permission: 'brokerage.deal_register',
      },
      // Party A and Party B — a master register of counterparties, with its own
      // module since 0042. It borrowed `brokerage.deal`, which answered "who may
      // add a counterparty" with "whoever may enter a deal" and left the screen
      // with no row of its own in the permission matrix.
      {
        key: 'deal-parties',
        label: 'Deal Parties',
        tag: 'PTY',
        permission: 'brokerage.deal_parties',
      },
      {
        key: 'availability',
        label: 'Product Availability',
        tag: 'MAP',
        permission: 'brokerage.availability',
      },
      {
        key: 'commission-invoice',
        label: 'Commission Invoice',
        tag: 'COM',
        permission: 'brokerage.commission_invoice',
      },
      {
        key: 'settlement',
        label: 'Pending Settlement',
        tag: 'SET',
        permission: 'brokerage.settlement',
      },
      // No Import Deal Tracker: 0021 dropped the shipment columns it read.
    ],
  },
  {
    key: 'finance',
    label: 'Finance & Accounts',
    hint: 'Vouchers, ledgers, statements',
    items: [
      {
        key: 'coa',
        label: 'Chart of Accounts',
        tag: 'COA',
        permission: 'finance.chart_of_accounts',
      },
      { key: 'jv', label: 'Journal Voucher', tag: 'JV', permission: 'finance.journal_voucher' },
      { key: 'cp', label: 'Cash Payment', tag: 'CP', permission: 'finance.cash_payment' },
      { key: 'cr', label: 'Cash Receipt', tag: 'CR', permission: 'finance.cash_receipt' },
      { key: 'bp', label: 'Bank Payment', tag: 'BP', permission: 'finance.bank_payment' },
      { key: 'br', label: 'Bank Receipt', tag: 'BR', permission: 'finance.bank_receipt' },
      { key: 'gl', label: 'General Ledger', tag: 'GL', permission: 'finance.general_ledger' },
      { key: 'tb', label: 'Trial Balance', tag: 'TB', permission: 'finance.trial_balance' },
      { key: 'pl', label: 'Profit & Loss', tag: 'P&L', permission: 'finance.profit_loss' },
      { key: 'bs', label: 'Balance Sheet', tag: 'BS', permission: 'finance.balance_sheet' },
      {
        key: 'recon',
        label: 'Bank Reconciliation',
        tag: 'REC',
        permission: 'finance.bank_reconciliation',
      },
    ],
  },
  {
    key: 'sales',
    label: 'Sales & Purchase',
    hint: 'Import, sales, commission',
    items: [
      { key: 'shipments', label: 'Import Shipments', tag: 'SHP', permission: 'purchase.shipment' },
      { key: 'costing', label: 'Import Costing Sheet', tag: 'CST', permission: 'purchase.costing' },
      { key: 'purchase', label: 'Purchase Entry', tag: 'PUR', permission: 'purchase.invoice' },
      { key: 'preturn', label: 'Purchase Return', tag: 'PRN', permission: 'purchase.return' },
      { key: 'invoice', label: 'Sales Invoice', tag: 'INV', permission: 'sales.invoice' },
      { key: 'sreturn', label: 'Sale Return', tag: 'SRN', permission: 'sales.return' },
      {
        key: 'commission-run',
        label: 'Commission Run',
        tag: 'RUN',
        permission: 'sales.commission_run',
      },
      { key: 'parties', label: 'Suppliers & Customers', tag: 'PTY', permission: 'master.party' },
    ],
  },
  {
    key: 'inventory',
    label: 'Inventory',
    hint: 'Products, warehouses, stock count',
    items: [
      { key: 'products', label: 'Product Management', tag: 'SKU', permission: 'inventory.product' },
      {
        key: 'categories',
        label: 'Product Categories',
        tag: 'CAT',
        permission: 'inventory.category',
      },
      { key: 'stock', label: 'Warehouse Inventory', tag: 'WH', permission: 'inventory.stock' },
      {
        key: 'stock-ledger',
        label: 'Stock Ledger',
        tag: 'LDG',
        permission: 'inventory.stock_ledger',
      },
      { key: 'transfer', label: 'Stock Transfer', tag: 'TRF', permission: 'inventory.transfer' },
      { key: 'count', label: 'Physical Count', tag: 'CNT', permission: 'inventory.count' },
      { key: 'batch', label: 'Batch & Expiry', tag: 'BCH', permission: 'inventory.batch' },
    ],
  },
  {
    key: 'users',
    label: 'Users & Zones',
    hint: 'Hierarchy-based access control',
    items: [
      { key: 'hierarchy', label: 'User Hierarchy', tag: 'USR', permission: 'admin.user' },
      { key: 'roles', label: 'Roles & Permissions', tag: 'ACL', permission: 'admin.role' },
      { key: 'zones', label: 'Zone Management', tag: 'ZON', permission: 'admin.zone' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    hint: 'Library, ageing, margin, audit trail',
    items: [
      { key: 'library', label: 'Report Library', tag: 'ALL', permission: 'reports.library' },
      { key: 'rpt-ageing', label: 'AR / AP Ageing', tag: 'AGE', permission: 'reports.financial' },
      { key: 'rpt-saleszone', label: 'Sales by Zone', tag: 'ZON', permission: 'reports.sales' },
      { key: 'rpt-margin', label: 'Gross Margin', tag: 'GM', permission: 'reports.management' },
      // The two sales-tax registers: one row per invoice line, in the shape the
      // return is filed from. Separate from the Sales Invoice and Purchase Entry
      // screens, which are where a *document* is entered and selected.
      {
        key: 'rpt-sales-register',
        label: 'Sales Register',
        tag: 'SRG',
        permission: 'reports.sales_register',
      },
      {
        key: 'rpt-purchase-register',
        label: 'Purchase Register',
        tag: 'PRG',
        permission: 'reports.purchase_register',
      },
      { key: 'rpt-audit', label: 'Audit Trail', tag: 'LOG', permission: 'admin.audit' },
    ],
  },
  {
    // The super administrator's module, and only theirs: `seed/0004_access.sql`
    // revokes the whole Setup permission group from every other role, and `App`
    // filters this entry on `admin.company`. Branches, currencies and units sit
    // here rather than under Master Data for that reason — an administrator runs
    // the application, and does not redefine what a year, a currency or a unit is.
    key: 'setup',
    label: 'Setup',
    hint: 'Company, calendar, numbering, masters',
    items: [
      { key: 'company', label: 'Company Profile', tag: 'CO', permission: 'admin.company' },
      {
        key: 'fy',
        label: 'Financial Year & Periods',
        tag: 'FY',
        permission: 'finance.period_control',
      },
      { key: 'numbering', label: 'Voucher Numbering', tag: 'NUM', permission: 'admin.numbering' },
      // Backed by `master.tax` (the read) and `finance.fx` (the rate writes),
      // both of which already have their own rows. It was `admin.currency`, which
      // is the *Currencies* screen below and governs neither.
      { key: 'fx', label: 'Tax & FX Settings', tag: 'FX', permission: 'master.tax' },
      { key: 'branches', label: 'Branches', tag: 'BR', permission: 'admin.branch' },
      { key: 'currencies', label: 'Currencies', tag: 'CUR', permission: 'admin.currency' },
      { key: 'uoms', label: 'Units of Measure', tag: 'UOM', permission: 'admin.uom' },
      // Gated on `admin.company` rather than a permission of its own: adding a
      // module code to `permission_modules` needs a seed, and a seed does not
      // re-run on a live book. This is the company's own posting policy, which is
      // the module it belongs to anyway.
      { key: 'links', label: 'Link Accounts', tag: 'LNK', permission: 'admin.account_links' },
    ],
  },
];

/**
 * The rail this session should see.
 *
 * An item is shown when its module is held; an item with no module is always
 * shown. A **group with nothing left in it is dropped entirely** rather than
 * rendered as a heading that expands onto nothing — the heading is a promise
 * that there is something under it.
 *
 * Pure, and returns the same array identity when nothing is filtered, so a
 * session holding everything costs React nothing.
 */
export function visibleModules(
  modules: NavModule[],
  can: (module: string, action?: string) => boolean,
): NavModule[] {
  const out: NavModule[] = [];
  let changed = false;

  for (const module of modules) {
    const items = module.items.filter((item) => !item.permission || can(item.permission));
    if (items.length === module.items.length) {
      out.push(module);
      continue;
    }
    changed = true;
    if (items.length === 0) continue;
    // `href` is the module's own address and is in practice its first item's, so
    // a group whose first item was filtered out must not keep pointing at it.
    out.push({ ...module, items, href: items[0]?.href ?? module.href });
  }

  return changed ? out : modules;
}

/** Which nav key each counted number belongs to. One place, so both agree. */
const BADGE_KEYS = {
  recon: 'recon',
  settlement: 'settlement',
} as const;

/**
 * The module map with live counts stamped onto the two items that carry one.
 *
 * Returns `MODULES` unchanged when there are no counts yet — the first render
 * happens before `/api/nav-counts` answers, and a rail that flashed a zero badge
 * and then a real one would be worse than a rail that waits. A count of **zero
 * clears the badge** rather than showing "0": nothing is waiting, and a badge
 * saying so is noise on every screen the accountant is not looking at.
 *
 * Pure, and copies only the module and item it touches, so React's identity
 * checks still hold for the seven modules that never change.
 */
export function withBadges(
  modules: NavModule[],
  counts: Partial<Record<keyof typeof BADGE_KEYS, number>> | undefined,
): NavModule[] {
  if (!counts) return modules;

  const byKey = new Map<string, number>();
  for (const [name, navKey] of Object.entries(BADGE_KEYS)) {
    const value = counts[name as keyof typeof BADGE_KEYS];
    if (typeof value === 'number' && value > 0) byKey.set(navKey, value);
  }
  if (byKey.size === 0) return modules;

  return modules.map((module) =>
    module.items.some((item) => byKey.has(item.key))
      ? {
          ...module,
          items: module.items.map((item) =>
            byKey.has(item.key) ? { ...item, badge: byKey.get(item.key) } : item,
          ),
        }
      : module,
  );
}
